<template>
    <main class="flex-1 relative pb-8 z-0 overflow-y-auto">
        <PageHeader>
            <h1 class="ml-3 text-2xl font-bold text-gray-900 sm:truncate flex flex-row justify-between w-full">
                <span>
                    <span class="flex flex-row items-center">
                        <router-link v-if="isRoot" :to="{ name: 'projects' }" class="text-cyan-800 hover:text-cyan-500">
                            Projets
                        </router-link>
                        <svg-vue icon="angle-right-b" class="h-6 w-6 fill-current"></svg-vue>
                        <router-link v-if="project" :to="{name: 'project-show', params: {id: project.id}}" class="text-cyan-800 hover:text-cyan-500">
                            {{ project ? project.translatable.name[locale] : '' }}
                        </router-link>
                        <svg-vue icon="angle-right-b" class="h-6 w-6 fill-current"></svg-vue>
                        <router-link v-if="building" :to="{name: 'building-show', params: {id: building.id}}" class="text-cyan-800 hover:text-cyan-500">
                            {{ building ? building.translatable.description[locale] : ''}}
                        </router-link>
                        <svg-vue icon="angle-right-b" class="h-6 w-6 fill-current"></svg-vue>
                            {{ floor ? floor.translatable.description[locale] : '' }}
                        <Loader v-if="isPending" class="h-6 w-6 m-2 border-2 -red -sm"/>
                    </span>
                    <LangSwitcher />
                </span>
                <span>
                    <a href="#" @click.prevent="handleCreateUnit"
                        class="flex items-center px-4 py-1 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-custom-600 hover:bg-custom-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500">
                        Créer une unité
                    </a>
                </span>
            </h1>
        </PageHeader>

        <div class="mt-8 lg:max-w-6xl lg:mx-auto">
            <h2 class="ml-3 text-1xl font-bold text-gray-900 sm:truncate flex flex-row justify-between w-full lg:mx-6">Unités</h2>
            <div class="m-4">
                <Search
                    v-model="search"
                    @update:modelValue="handleSearchChange"
                    class="lg:mx-6"
                />

                <div class="flex flex-col items-center justify-center">
                    <Loader v-if="isUnitsFetchPending" class="m-2 border-2 -red"/>

                    <div v-else class="-my-2 sm:-mx-6 lg:-mx-6 w-full">
                        <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8 min-h-[50vh]">
                            <div class="shadow border-b border-gray-200 sm:rounded-lg">
                                <table class="min-w-full divide-y divide-gray-200 table-fixed">
                                    <thead>
                                    <tr>
                                        <th scope="col" class="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase w-1/6">
                                            Name
                                        </th>
                                        <th scope="col" class="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase w-1/6">
                                            Status
                                        </th>
                                        <th scope="col" class="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase w-1/6">
                                            Surface
                                        </th>
                                        <th scope="col" class="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase w-1/6">
                                            Prix
                                        </th>
                                        <th scope="col" class="px-6 py-3 bg-gray-50 uppercase w-1/5">
                                            <span class="sr-only">Action(s)</span>
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="(unit, index) in units" :class="index%2 ? 'bg-gray-50' : 'bg-white'" :key="unit.id ? unit.id : index">
                                        <td class="px-6 py-4 text-sm text-gray-500">
                                            {{ unit.translatable?.fullname[locale] ? unit.translatable?.fullname[locale] : unit.name }}
                                        </td>
                                        <td class="px-6 py-4 text-sm text-gray-500">
                                            <Select
                                                id="status_id"
                                                name="status_id"
                                                :options="statusOptions"
                                                v-model="unit.status_id"
                                                :errors="errors.status"
                                                @update:modelValue="handleSaveUnit(unit)"
                                            />
                                        </td>
                                        <td class="px-6 py-4 text-sm text-gray-500">
                                            <Input
                                                id="surface"
                                                type="number"
                                                name="surface"
                                                placeholder="10"
                                                v-model="unit.surface"
                                                :errors="errors.surface"
                                                unit="m²"
                                                @change="handleSaveUnit(unit)"
                                            />
                                        </td>
                                        <td class="px-6 py-4 text-sm text-gray-500">
                                            <Input
                                                id="price"
                                                type="number"
                                                name="price"
                                                placeholder="10"
                                                v-model="unit.price"
                                                :errors="errors.price"
                                                unit="€"
                                                @change="handleSaveUnit(unit)"
                                            />
                                        </td>
                                        <td class="px-6 py-4 text-right text-sm font-medium">
                                            <router-link v-if="is360ViewAvailable(unit.name)" target="_blank" :to="{ name: 'unit-v360', params: {id: unit.id} }" class="text-indigo-600 hover:text-indigo-900 px-2">Voir 360</router-link>
                                            <router-link :to="{ name: 'unit-save', params: {id: unit.id} }" class="text-indigo-600 hover:text-indigo-900 px-2">Edit</router-link>
                                            <a href="/delete" class="text-red-600 hover:text-red-900" @click.prevent="handleDeleteUnit(unit)">Delete</a>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <Pagination
                    v-if="lastPage > 1"
                    :current_project="currentPage"
                    :last_page="lastPage"
                    @change="handleChangePage"
                />
            </div>
        </div>
    </main>
</template>

<script>
import PageHeader from "../../components/partials/page-header";
import Loader from "../../components/partials/loader";
import LangSwitcher from "../../components/partials/lang-switcher";
import List from "../../components/partials/list"
import Pagination from "../../components/partials/pagination"
import Search from "../../components/partials/search"
import Input from "../../components/form/input"
import Select from "../../components/form/select"
import PROJECT_TOPOLOGY from "../../enums/project-topology";
import Switch from "../../components/form/switch"
import USER_ROLE from "../../enums/user-role";
import Axios from 'axios';

export default {
    data() {
        return {
            search: null,
            desc: false,
            PROJECT_TOPOLOGY: PROJECT_TOPOLOGY,
            view360Cache: new Map(),
        }
    },
    async beforeMount() {
        this.$store.dispatch('floors/getSingle', {id: this.$route.params.id, includes: ['building', 'building.project']})
        this.$store.dispatch('units/getList', {search: {floor_id: this.$route.params.id}})
        this.$store.dispatch('statuses/getList');
    },
    async mounted() {
        // Attendre que les données soient chargées
        await this.$nextTick();

        // Vérifier la disponibilité des vues 360 pour toutes les unités
        if (this.units && this.units.length > 0) {
            await this.checkAll360ViewAvailability();
        }
    },
    computed: {
        isUnitsFetchPending() {
            return this.$store.state.units.isFetchListPending
        },
        isFloorFetchPending() {
            return this.$store.state.floors.isFetchSinglePending
        },
        isPending() {
            return this.isFloorFetchPending || this.isUnitsFetchPending;
        },
        errors() {
            return this.$store.state.units.errors;
        },
        locale() {
            return this.$store.state.config.locale;
        },
        units() {
            return this.$store.state.units.list;
        },
        lastPage() {
            return this.$store.state.units.pagination.last_page
        },
        currentPage() {
            return this.$store.state.units.pagination.current_page
        },
        statuses() {
            return this.$store.state.statuses.list;
        },
        authUser() {
            return this.$store.state.auth.auth_user;
        },
        statusOptions() {
            return this.statuses.map((status) => {
                return {label: status.translatable.name[this.locale], value: status.id}
            })
        },
        floor() {
            return this.$store.state.floors.single;
        },
        building() {
            return this.floor ? this.floor.building : null;
        },
        project() {
            return this.building ? this.building.project : null;
        },
        isRoot() {
            if(this.authUser) {
                return Number(this.authUser.role) === Number(USER_ROLE.values.ROOT.description)
            }

            return false
        },
    },
    methods: {
        handleDeleteUnit(unit) {
            this.$store.state.units.single = unit;
            this.$store.dispatch('modals/open', 'unit-delete');
        },
        handleChangePage(page = this.currentPage) {
            this.$store.dispatch('units/getList', {
                'page': page,
                'sort_by': this.sort_by,
                'desc': this.desc,
                'search': this.search ? {'name': this.search, floor_id: this.$route.params.id} : {floor_id: this.$route.params.id}
            });
        },
        handleSortBy(type) {
            this.desc = !this.desc;
            this.sort_by = type;

            this.handleChangePage();
        },
        handleSearchChange(search) {
            this.search = search;
            this.handleChangePage();
        },
        handleCreateUnit() {
            this.$router.push({name: 'unit-save'});
        },
        handleEditUnit(unit) {
            this.$store.dispatch('upload/resetList')
            this.$router.push({name: 'unit-save', params: {id: unit.id}})
        },
        handleSaveUnit(unit) {
            this.$store.dispatch('units/update', {single: {
                ...unit,
                blueprints2D: [],
                blueprint3D: null,
                blueprints: null,
                description: {
                    fr: unit.translatable.description.fr,
                    en: unit.translatable.description.en,
                },
            }})
        },
        async check360ViewAvailability(unit_name) {
            const cacheKey = `${this.project?.name?.toLowerCase()}_${this.building?.name?.toLowerCase()}_${unit_name?.toLowerCase()}`;

            if (this.view360Cache.has(cacheKey)) {
                return this.view360Cache.get(cacheKey);
            }

            try {
                const response = await Axios.get(`${process.env.MIX_APP_URL}/storage/v360/${this.project?.name?.toLowerCase()}/${this.building?.name?.toLowerCase()}/${unit_name?.toLowerCase()}/config.json`);

                const isAvailable = response?.data?.totalImages ? true : false;

                this.view360Cache.set(cacheKey, isAvailable);

                return isAvailable;
            } catch (error) {
                console.warn(`Erreur lors de la vérification de la vue 360 pour ${unit_name}:`, error);

                this.view360Cache.set(cacheKey, false);

                return false;
            }
                },
        async checkAll360ViewAvailability() {
            if (!this.units || this.units.length === 0) return;

            const promises = this.units.map(async (unit) => {
                await this.check360ViewAvailability(unit.name);
            });

            await Promise.all(promises);
        },
        is360ViewAvailable(unit_name) {
            const cacheKey = `${this.project?.name?.toLowerCase()}_${this.building?.name?.toLowerCase()}_${unit_name?.toLowerCase()}`;
            return this.view360Cache.get(cacheKey) || false;
        }
    },
    watch: {
        units: {
            handler: async function(newUnits) {
                if (newUnits && newUnits.length > 0) {
                    await this.checkAll360ViewAvailability();
                }
            },
            deep: true
        }
    },
    components: {
        PageHeader,
        Loader,
        LangSwitcher,
        Search,
        List,
        Pagination,
        Input,
        Select,
        Switch
    }
}
</script>
